import { useParams, Link } from "react-router-dom";
import ArticleLayout from "@/components/feature/ArticleLayout";
import { getArticleBySlug, allArticles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

function ArticleContent({ slug }: { slug: string }) {
  switch (slug) {
    case "oopbuy-spreadsheet-guide":
      return <GuideContent />;
    case "oopbuy-spreadsheet-anfaenger":
      return <AnfaengerContent />;
    case "oopbuy-spreadsheet-qc":
      return <QCContent />;
    case "oopbuy-spreadsheet-versand":
      return <VersandContent />;
    case "oopbuy-spreadsheet-sneaker":
      return <SneakerContent />;
    case "oopbuy-spreadsheet-coupons":
      return <CouponsContent />;
    case "oopbuy-spreadsheet-vergleich":
      return <VergleichContent />;
    case "oopbuy-spreadsheet-sicherheit":
      return <SicherheitContent />;
    case "oopbuy-spreadsheet-taobao":
      return <TaobaoContent />;
    case "oopbuy-spreadsheet-erfahrungen":
      return <ErfahrungenContent />;
    default:
      return <GuideContent />;
  }
}

export default function GuidePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : allArticles[0];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Artikel nicht gefunden</h1>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">
            Zurück zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <ArticleLayout article={article}>
        <ArticleContent slug={article.slug} />
      </ArticleLayout>
    </>
  );
}

function GuideContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        Willkommen zum umfassendsten deutschsprachigen Guide zur{" "}
        <strong>OOPBUY Spreadsheet</strong>. Egal, ob du gerade erst anfängst oder
        bereits Erfahrung mit chinesischen Shopping-Agenten hast — in diesem Artikel
        erfährst du alles, was du brauchst, um die besten Deals zu finden, Qualität zu
        prüfen und sicher nach Deutschland zu liefern.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Was ist OOPBUY und warum ist es so beliebt?
      </h2>
      <p>
        <strong>OOPBUY</strong> ist einer der führenden Shopping-Agenten für chinesische
        Marktplätze wie Taobao, Weidian und 1688. Die Plattform fungiert als
        Zwischenhändler: Du bestellst Produkte auf chinesischen Websites, OOPBUY kauft
        sie für dich ein, prüft die Qualität per Fotos (QC) und versendet das Paket an
        deine deutsche Adresse. Besonders bei Sneakern, Streetwear und Accessoires hat
        sich OOPBUY einen Namen gemacht.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Was genau ist eine OOPBUY Spreadsheet?
      </h2>
      <p>
        Eine <strong>OOPBUY Spreadsheet</strong> ist eine öffentlich zugängliche,
        strukturierte Liste empfohlener Produkte und Verkäufer. Statt stundenlang Taobao
        nach vertrauenswürdigen Shops zu durchsuchen, findest du in einer gut gepflegten
        Spreadsheet bereits vorgefilterte Artikel mit direkten Links, Preisangaben,
        Verkäuferbewertungen und QC-Fotos.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Schritt für Schritt: So nutzt du die OOPBUY Spreadsheet
      </h2>
      <p><strong>Schritt 1 — Durchsuchen:</strong> Öffne die Spreadsheet und filtere nach deiner gewünschten Kategorie.</p>
      <p><strong>Schritt 2 — Produkt auswählen:</strong> Klicke auf den Link und lege das Produkt in deinen OOPBUY-Warenkorb.</p>
      <p><strong>Schritt 3 — Bestellen:</strong> Wähle Größe und Farbe und bezahle über die verfügbaren Zahlungsmethoden.</p>
      <p><strong>Schritt 4 — QC-Fotos abwarten:</strong> Prüfe die Qualitätsfotos sorgfältig, bevor du den Versand bestätigst.</p>
      <p><strong>Schritt 5 — Versand bestätigen:</strong> Wähle eine Versandart und warte auf dein Paket in Deutschland.</p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Versand nach Deutschland: Was du wissen musst
      </h2>
      <p>
        Der Versand von China nach Deutschland dauert je nach Methode 7–20 Tage.
        OOPBUY bietet verschiedene Versandlinien an, darunter DHL, EMS und spezielle
        Tax-Free-Lines für europäische Länder.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        QC-Prozess: Qualitätskontrolle bei OOPBUY
      </h2>
      <p>
        Der <strong>QC-Prozess</strong> ist einer der größten Vorteile von OOPBUY. Nach
        Eintreffen deiner Ware im Lager werden detaillierte Fotos aus allen Winkeln gemacht,
        sodass du vor dem internationalen Versand die Qualität prüfen kannst.
      </p>

      <p className="mt-6">
        Nutze die{" "}
        <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">
          OOPBUY Spreadsheet auf unserer Startseite
        </Link>{" "}
        und entdecke über 3.000 verifizierte Produkte.{" "}
        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">
          Jetzt bei OOPBUY einkaufen
        </a>.
      </p>
    </>
  );
}

function AnfaengerContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        Du möchtest mit <strong>OOPBUY</strong> starten, weißt aber nicht wie? Kein Problem!
        In diesem Guide nehmen wir dich an die Hand und führen dich von null zum ersten
        erfolgreichen Einkauf. Folgende einfach unserer Schritt-für-Schritt-Anleitung.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Schritt 1: OOPBUY-Konto erstellen
      </h2>
      <p>
        Besuche die offizielle OOPBUY-Website und klicke auf „Registrieren". Du benötigst
        nur eine gültige E-Mail-Adresse. Nach der Bestätigung kannst du direkt loslegen.
        Der gesamte Vorgang dauert weniger als <strong>drei Minuten</strong>.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Schritt 2: Die Spreadsheet verstehen
      </h2>
      <p>
        Unsere <strong>OOPBUY Spreadsheet</strong> ist dein persönlicher Einkaufsassistent.
        Sie enthält über 3.000 Produkte von verifizierten Verkäufern, sortiert in Kategorien
        wie Schuhe, Kleidung, Accessoires und mehr. Jeder Eintrag hat einen direkten Link
        zum Produkt auf Taobao oder Weidian.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Schritt 3: Erstes Produkt finden und bestellen
      </h2>
      <p>
        Wähle eine Kategorie aus der Spreadsheet — zum Beispiel{" "}
        <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">Shoes</a>{" "}
        oder{" "}
        <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">T-Shirts</a>.
        Klicke auf einen Produktlink, kopiere die URL und füge sie in die OOPBUY-Suchleiste ein.
        Wähle Größe und Farbe, bezahle — fertig!
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Schritt 4: QC-Fotos verstehen
      </h2>
      <p>
        Sobald deine Bestellung im OOPBUY-Lager eintrifft, erhältst du <strong>QC-Fotos</strong>.
        Diese zeigen dir das tatsächliche Produkt aus verschiedenen Winkeln. Prüfe genau:
        Stimmen Farbe, Größe und Qualität? Falls nicht, kannst du das Produkt kostenlos zurücksenden.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Schritt 5: Versand nach Deutschland
      </h2>
      <p>
        Bist du zufrieden, bestätigst du den internationalen Versand. Für Anfänger empfehlen
        wir <strong>DHL</strong> oder eine <strong>Tax-Free-Line</strong> — beide sind zuverlässig
        und bieten gutes Tracking. Die Lieferzeit beträgt meist 10–14 Tage.
      </p>

      <p className="mt-6">
        Bereit zum Start?{" "}
        <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">
          Durchstöbere die OOPBUY Spreadsheet
        </Link>{" "}
        und finde dein erstes Produkt!
      </p>
    </>
  );
}

function QCContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        Der <strong>QC-Prozess</strong> (Quality Control) ist das Herzstück des OOPBUY-Erlebnisses.
        Hier erfährst du, wie du QC-Fotos richtig liest und Fälschungen oder Qualitätsmängel
        sofort erkennst.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Was sind QC-Fotos?
      </h2>
      <p>
        QC steht für <strong>Quality Control</strong>. Sobald dein bestelltes Produkt im
        OOPBUY-Lager in China ankommt, macht das Team mehrere hochauflösende Fotos aus
        verschiedenen Perspektiven. Du siehst Vorder- und Rückseite, Details wie Nähte,
        Logos und Etiketten sowie die Verpackung.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Worauf du bei QC-Fotos achten musst
      </h2>
      <p><strong>1. Farbe und Material:</strong> Vergleiche die Fotos mit den Produktbildern des Verkäufers. Farbabweichungen sind normal, starke Unterschiede jedoch ein Warnsignal.</p>
      <p><strong>2. Nähte und Verarbeitung:</strong> Lose Fäden, schiefe Nähte oder Klebereste deuten auf minderwertige Qualität hin.</p>
      <p><strong>3. Logos und Etiketten:</strong> Prüfe, ob das Markenlogo korrekt platziert und scharf gestickt oder gedruckt ist.</p>
      <p><strong>4. Größenlabel:</strong> Vergewissere dich, dass die bestellte Größe mit dem Etikett übereinstimmt.</p>
      <p><strong>5. Verpackung:</strong> Ist die Originalverpackung intakt? Bei Sneakern sollte der Schuhkarton unbeschädigt sein.</p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Was tun bei Qualitätsmängeln?
      </h2>
      <p>
        Entdeckst du Mängel, kannst du das Produkt <strong>kostenlos zurückgeben</strong> oder
        umtauschen lassen. OOPBUY erstattet in den meisten Fällen den vollen Kaufpreis.
        Wichtig: Reagiere schnell — du hast meist nur <strong>72 Stunden</strong> nach Erhalt
        der QC-Fotos, um eine Rückgabe zu beantragen.
      </p>

      <p className="mt-6">
        Mehr zum Thema? Lies unseren{" "}
        <Link to="/guide/oopbuy-spreadsheet-anfaenger" className="text-primary-600 hover:text-primary-700 underline">
          Anfänger-Guide
        </Link>{" "}
        oder{" "}
        <Link to="/" className="text-primary-600 hover:text-primary-700 underline">
          entdecke die Spreadsheet
        </Link>.
      </p>
    </>
  );
}

function VersandContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        Der Versand von China nach Deutschland ist für viele OOPBUY-Neulinge die größte
        Unsicherheit. Keine Sorge — mit diesem Guide behältst du den Durchblick.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Versandoptionen im Überblick
      </h2>
      <p><strong>DHL:</strong> 7–14 Tage, sehr zuverlässig, gutes Tracking. Empfohlen für die meisten Bestellungen.</p>
      <p><strong>EMS:</strong> 10–20 Tage, günstiger als DHL, Tracking etwas langsamer.</p>
      <p><strong>EUB:</strong> 15–30 Tage, die günstigste Option, nur für kleine Pakete.</p>
      <p><strong>Tax-Free-Lines:</strong> 10–18 Tage, speziell für EU-Länder optimiert. Geringeres Zollrisiko.</p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Zoll und Einfuhrgebühren
      </h2>
      <p>
        Bei Warenwerten über <strong>150 Euro</strong> fallen in Deutschland Zollgebühren an.
        Darunter ist die Einfuhr in der Regel zollfrei. OOPBUY deklariert den Warenwert auf
        dem Paket — du kannst den deklarierten Betrag vor dem Versand selbst festlegen.
        Sei jedoch vorsichtig mit zu niedrigen Angaben, da dies zu Problemen führen kann.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Versandkosten sparen
      </h2>
      <p>
        Sammle mehrere Artikel und versende sie <strong>gebündelt</strong> — das spart
        Versandkosten. OOPBUY bietet zudem regelmäßig Versandgutscheine für Neukunden an.
        Und: Je leichter das Paket, desto günstiger der Versand. Entferne unnötige
        Verpackungen vor dem internationalen Versand.
      </p>

      <p className="mt-6">
        <Link to="/guide/oopbuy-spreadsheet-coupons" className="text-primary-600 hover:text-primary-700 underline">
          Coupons & Rabatte entdecken
        </Link>{" "}
        und Versandkosten sparen!
      </p>
    </>
  );
}

function SneakerContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        Sneaker sind die absolute <strong>#1 Kategorie</strong> in der OOPBUY Spreadsheet.
        Egal ob Nike, Adidas, Jordan oder Yeezy — hier findest du die besten Verkäufer und
        aktuellsten Deals 2026.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Warum Sneaker über OOPBUY kaufen?
      </h2>
      <p>
        Viele limitierte Modelle sind in Europa ausverkauft oder stark überteuert. Über
        OOPBUY und chinesische Marktplätze bekommst du Zugang zu exklusiven Releases zu
        deutlich fairen Preisen. Die Spreadsheet listet nur <strong>verifizierte Verkäufer</strong>{" "}
        mit nachgewiesener Qualität.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Beliebte Sneaker-Marken in der Spreadsheet
      </h2>
      <p>Die am häufigsten gesuchten Sneaker-Marken in unserer Spreadsheet sind <strong>Nike Dunk</strong>, <strong>Air Jordan 1</strong> und <strong>4</strong>, <strong>Adidas Yeezy</strong>, <strong>New Balance</strong> und <strong>Balenciaga</strong>. Besonders die Dunk-Serie erlebt 2026 einen riesigen Hype.</p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        So findest du die besten Sneaker-Deals
      </h2>
      <p><strong>1. Täglich die Spreadsheet checken:</strong> Neue Links werden täglich hinzugefügt.</p>
      <p><strong>2. QC-Fotos genau prüfen:</strong> Achte auf Sohlenstiche, Zehenbox und Materialqualität.</p>
      <p><strong>3. Verkäuferbewertungen lesen:</strong> Kaufe nur bei Händlern mit mindestens 95 % positiven Bewertungen.</p>

      <p className="mt-6">
        <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline font-medium">
          Jetzt Sneaker durchstöbern
        </a>{" "}
        auf kaspreadsheet.com
      </p>
    </>
  );
}

function CouponsContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        Wer bei OOPBUY einkauft, sollte niemals den vollen Preis zahlen. Mit den richtigen{" "}
        <strong>Coupons und Rabatten</strong> sparst du 10–30 % — wir zeigen dir wie.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Wo finde ich OOPBUY Coupons?
      </h2>
      <p>OOPBUY veröffentlicht Coupons an mehreren Stellen: im <strong>Dashboard</strong> unter „Meine Gutscheine", per <strong>E-Mail-Newsletter</strong> und über die <strong>offizielle App</strong>. Besonders Neukunden erhalten oft einen Willkommensgutschein über 5–10 €.</p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Spar-Tricks für Fortgeschrittene
      </h2>
      <p><strong>Cashback-Aktionen:</strong> OOPBUY bietet regelmäßig Cashback auf Versandkosten. Nach deiner Bestellung bekommst du einen Teil der Gebühren als Guthaben zurück.</p>
      <p><strong>Saisonale Sales:</strong> Zu Feiertagen wie dem chinesischen Neujahr oder dem 11.11 (Singles Day) gibt es massive Rabatte.</p>
      <p><strong>Sammelbestellungen:</strong> Mehrere Artikel in einem Paket zu versenden spart nicht nur Versand, sondern aktiviert oft zusätzliche Mengenrabatte.</p>

      <p className="mt-6">
        <Link to="/guide/oopbuy-spreadsheet-versand" className="text-primary-600 hover:text-primary-700 underline">
          Versand-Guide lesen
        </Link>{" "}
        für mehr Spar-Tipps!
      </p>
    </>
  );
}

function VergleichContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        OOPBUY ist nicht der einzige Shopping-Agent. Aber ist es der <strong>beste</strong>?
        Wir vergleichen OOPBUY mit Pandabuy, Wegobuy und CSSBuy in allen wichtigen Kategorien.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        OOPBUY vs Pandabuy
      </h2>
      <p>
        Pandabuy war lange der Platzhirsch, wurde jedoch 2024 eingestellt. OOPBUY hat viele
        ehemalige Pandabuy-Nutzer übernommen und bietet eine sehr ähnliche Benutzeroberfläche.
        Vorteil OOPBUY: bessere Versandpreise nach Deutschland und schnellere QC-Fotos.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        OOPBUY vs Wegobuy
      </h2>
      <p>
        Wegobuy ist der älteste Agent, aber die Plattform fühlt sich veraltet an. OOPBUY
        punktet mit modernerem Design, schnellerem Kundenservice und wettbewerbsfähigeren
        Versandoptionen speziell für den deutschen Markt.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Fazit: Warum OOPBUY die beste Wahl ist
      </h2>
      <p>
        Für deutsche Käufer ist OOPBUY 2026 die <strong>beste Wahl</strong>: intuitive
        Bedienung, günstige EU-Versandlinien, zuverlässige QC-Fotos und aktive
        Community.{" "}
        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">
          Jetzt OOPBUY ausprobieren
        </a>.
      </p>
    </>
  );
}

function SicherheitContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        Sicherheit steht beim Online-Shopping aus China an erster Stelle. Mit diesen Tipps
        vermeidest du <strong>Betrug und Scams</strong> auf OOPBUY.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        So erkennst du seriöse Verkäufer
      </h2>
      <p><strong>Bewertungen checken:</strong> Kaufe nur bei Verkäufern mit mindestens 4,5 Sternen und vielen Transaktionen.</p>
      <p><strong>QC-Fotos verlangen:</strong> Jeder seriöse Verkäufer akzeptiert QC. Wer ablehnt, hat etwas zu verbergen.</p>
      <p><strong>Spreadsheet nutzen:</strong> Unsere kuratierte Liste enthält ausschließlich geprüfte Verkäufer.</p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Häufige Betrugsmaschen
      </h2>
      <p><strong>Bait-and-Switch:</strong> Der Verkäufer zeigt Premium-Fotos, liefert aber minderwertige Ware. QC-Fotos verhindern das.</p>
      <p><strong>Gefälschte Tracking-Nummern:</strong> Immer über OOPBUY bestellen — nie direkt beim Verkäufer. OOPBUY garantiert die Echtheit.</p>
      <p><strong>Phishing-Mails:</strong> Öffne OOPBUY nur über die offizielle URL, nicht über E-Mail-Links.</p>

      <p className="mt-6">
        <Link to="/guide/oopbuy-spreadsheet-qc" className="text-primary-600 hover:text-primary-700 underline">
          QC-Guide lesen
        </Link>{" "}
        für mehr Sicherheit beim Einkauf.
      </p>
    </>
  );
}

function TaobaoContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        Taobao und Weidian sind die <strong>größten Marktplätze Chinas</strong> — aber für
        europäische Käufer oft verwirrend. Mit diesem Guide navigierst du sie sicher.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Taobao vs Weidian: Was ist der Unterschied?
      </h2>
      <p>
        <strong>Taobao</strong> ist das Amazon Chinas — riesig, mit allem von Elektronik bis
        Mode. <strong>Weidian</strong> ist kleiner, fokussiert auf Streetwear und Sneaker,
        und oft günstiger. Beide Plattformen sind komplett auf Chinesisch — aber keine Sorge,
        unsere Spreadsheet enthält die direkten Links.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        So nutzt du Taobao mit OOPBUY
      </h2>
      <p>
        Du brauchst kein Taobao-Konto! Kopiere einfach den Produktlink aus unserer Spreadsheet
        und füge ihn in die OOPBUY-Suchleiste ein. OOPBUY kümmert sich um den gesamten
        Kaufprozess — du musst kein Wort Chinesisch können.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Die besten Shops auf einen Blick
      </h2>
      <p>
        Die Spreadsheet listet Shops für jede Kategorie:{" "}
        {categories.slice(0, 4).map((cat, i) => (
          <span key={cat.name}>
            <a href={cat.url} target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">{cat.name}</a>
            {i < 3 ? ", " : ""}
          </span>
        ))}{" "}
        und viele mehr. Jeder Shop wurde von unserem Team geprüft.
      </p>

      <p className="mt-6">
        <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">
          Zur Spreadsheet — Jetzt Shops entdecken
        </Link>
      </p>
    </>
  );
}

function ErfahrungenContent() {
  return (
    <>
      <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
        Wie gut ist OOPBUY wirklich? Wir haben <strong>echte deutsche Käufer</strong> befragt
        und ihre ungefilterten Erfahrungen gesammelt.
      </p>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Das sagen deutsche OOPBUY-Nutzer
      </h2>
      <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 my-4">
        <p className="text-sm text-foreground-700 italic mb-2">
          „Ich war anfangs skeptisch, aber die Spreadsheet hat mir Stunden an Recherche gespart.
          Die QC-Fotos waren super detailliert und meine Sneaker kamen nach 12 Tagen in perfektem
          Zustand an. Absolut empfehlenswert!"
        </p>
        <p className="text-xs text-foreground-500">— Markus K., Berlin</p>
      </div>
      <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 my-4">
        <p className="text-sm text-foreground-700 italic mb-2">
          „Der Versand mit der Tax-Free-Line war problemlos. Kein Zoll, gute Tracking-Infos.
          Einziger Minuspunkt: Der Kundenservice antwortet manchmal erst nach 24 Stunden."
        </p>
        <p className="text-xs text-foreground-500">— Sarah M., München</p>
      </div>
      <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 my-4">
        <p className="text-sm text-foreground-700 italic mb-2">
          „Die OOPBUY Spreadsheet ist Gold wert. Ich habe mehrere Hauls gemacht und jedes Mal
          die QC-Fotos vor dem Versand geprüft. Bisher nur positive Erfahrungen."
        </p>
        <p className="text-xs text-foreground-500">— Thomas W., Hamburg</p>
      </div>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
        Fazit der Community
      </h2>
      <p>
        Die überwältigende Mehrheit der deutschen OOPBUY-Nutzer ist zufrieden. Die größten
        Pluspunkte: riesige Produktauswahl, verlässliche QC-Fotos und gute Versandoptionen.
        Kleinere Kritikpunkte: Der Kundenservice könnte schneller sein und die App
        gelegentlich kleine Übersetzungsfehler enthalten.
      </p>

      <p className="mt-6">
        <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">
          Selbst ausprobieren — Starte mit der Spreadsheet
        </Link>
      </p>
    </>
  );
}